import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { UserContext } from '../../context/UserContext';
import { apiFetch, getAuthUserId } from '../../utils/apiClient';

export const MessagesContext = createContext(null);

const IDLE_POLL_MS = 45000;
const OPEN_POLL_MS = 15000;

export function useMessages() {
    const context = useContext(MessagesContext);
    if (!context) {
        throw new Error('useMessages must be used within MessagesProvider');
    }
    return context;
}

async function chatPost(path, body) {
    const res = await apiFetch(`/MarketplaceChat/${path}`, { body });
    const text = await res.text();
    let data = null;
    if (text) {
        try {
            data = JSON.parse(text);
        } catch {
            data = null;
        }
    }
    if (!res.ok) {
        const validationErrors = data?.errors;
        let validationMessage = '';
        if (validationErrors && typeof validationErrors === 'object') {
            validationMessage = Object.values(validationErrors)
                .flat()
                .filter(Boolean)
                .join(' ');
        }
        const message =
            data?.error ||
            data?.Error ||
            validationMessage ||
            data?.detail ||
            data?.title ||
            `MarketplaceChat/${path} failed: ${res.status}`;
        const error = new Error(message);
        error.status = res.status;
        error.data = data;
        throw error;
    }
    return data;
}

function conversationIdFrom(data) {
    const raw = data?.conversationId ?? data?.ConversationId;
    const id = Number(raw);
    return Number.isFinite(id) && id > 0 ? id : null;
}

/** Inbox rows with no message preview are empty shells — never show them. */
function withMessagesOnly(list) {
    if (!Array.isArray(list)) return [];
    return list.filter((conversation) => {
        const preview = conversation?.lastMessagePreview ?? conversation?.LastMessagePreview ?? '';
        return String(preview).trim().length > 0;
    });
}

export const MessagesProvider = ({ children }) => {
    const { user } = useContext(UserContext);
    const [conversations, setConversations] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [activeConversationId, setActiveConversationId] = useState(null);
    /** Compose UI before first send — no DB row until a message is sent. */
    const [draftConversation, setDraftConversation] = useState(null);
    const [loading, setLoading] = useState(false);
    const inFlightRef = useRef(false);
    const fetchConversationsRef = useRef(null);

    const fetchConversations = useCallback(async ({ force = false } = {}) => {
        if (!user?.userId) return [];
        if (typeof document !== 'undefined' && document.hidden) return [];
        if (inFlightRef.current && !force) return [];
        inFlightRef.current = true;
        try {
            const data = await chatPost('GetConversations', { userId: String(user.userId) });
            const list = withMessagesOnly(Array.isArray(data) ? data : []);
            setConversations(list);
            return list;
        } catch (error) {
            console.error('Failed to load conversations:', error);
            return [];
        } finally {
            inFlightRef.current = false;
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        fetchConversationsRef.current = fetchConversations;
    }, [fetchConversations]);

    // Initial load when user logs in
    useEffect(() => {
        if (!user?.userId) {
            setConversations([]);
            setIsOpen(false);
            setActiveConversationId(null);
            setDraftConversation(null);
            return undefined;
        }
        setLoading(true);
        fetchConversations();
        return undefined;
    }, [user?.userId, fetchConversations]);

    // Single poll loop: rate depends on drawer open; pauses when tab hidden
    useEffect(() => {
        if (!user?.userId) return undefined;

        let intervalId = null;

        const clearPoll = () => {
            if (intervalId != null) {
                clearInterval(intervalId);
                intervalId = null;
            }
        };

        const tick = () => {
            if (typeof document !== 'undefined' && document.hidden) return;
            fetchConversationsRef.current?.();
        };

        const startPoll = () => {
            clearPoll();
            if (typeof document !== 'undefined' && document.hidden) return;
            const ms = isOpen ? OPEN_POLL_MS : IDLE_POLL_MS;
            intervalId = setInterval(tick, ms);
        };

        const onVisibility = () => {
            if (document.hidden) {
                clearPoll();
            } else {
                tick();
                startPoll();
            }
        };

        startPoll();
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            clearPoll();
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [user?.userId, isOpen]);

    const unreadTotal = useMemo(
        () => conversations.reduce((sum, conversation) => sum + (conversation.unreadCount || 0), 0),
        [conversations]
    );

    const openMessages = useCallback((conversationId) => {
        setDraftConversation(null);
        setIsOpen(true);
        setActiveConversationId(conversationId ?? null);
        fetchConversations();
    }, [fetchConversations]);

    const closeMessages = useCallback(() => {
        setIsOpen(false);
        setActiveConversationId(null);
        setDraftConversation(null);
    }, []);

    const clearDraftConversation = useCallback(() => {
        setDraftConversation(null);
    }, []);

    const toggleMessages = useCallback(() => {
        setIsOpen((open) => {
            if (open) {
                setActiveConversationId(null);
                setDraftConversation(null);
                return false;
            }
            fetchConversations();
            return true;
        });
    }, [fetchConversations]);

    /**
     * Opens the compose UI for a recipient without creating a DB conversation.
     * If a real chat with that person already exists, opens that instead.
     */
    const openDraftConversation = useCallback(async ({
        otherUserId,
        otherUsername,
        marketplaceItemId,
        collectionItemId,
        itemTitle,
    } = {}) => {
        const authUserId = getAuthUserId() ?? Number(user?.userId);
        if (!Number.isFinite(authUserId) || authUserId <= 0) {
            throw new Error('Please log in to send a message.');
        }

        const otherId = Number(otherUserId);
        if (!Number.isFinite(otherId) || otherId <= 0) {
            throw new Error('Missing recipient for this conversation.');
        }
        if (otherId === authUserId) {
            throw new Error('You cannot message yourself.');
        }

        const list = await fetchConversations({ force: true });
        const existing = (list || conversations).find(
            (conversation) => Number(conversation.otherUserId ?? conversation.OtherUserId) === otherId
        );
        const existingId = Number(existing?.conversationId ?? existing?.ConversationId);
        if (Number.isFinite(existingId) && existingId > 0) {
            setDraftConversation(null);
            setIsOpen(true);
            setActiveConversationId(existingId);
            return { conversationId: existingId, isDraft: false };
        }

        setActiveConversationId(null);
        setDraftConversation({
            otherUserId: otherId,
            otherUsername: otherUsername || 'User',
            marketplaceItemId: Number(marketplaceItemId) > 0 ? Number(marketplaceItemId) : undefined,
            collectionItemId: Number(collectionItemId) > 0 ? Number(collectionItemId) : undefined,
            itemTitle: itemTitle || '',
        });
        setIsOpen(true);
        return { conversationId: null, isDraft: true };
    }, [user, fetchConversations, conversations]);

    const startConversation = useCallback(async ({
        otherUserId,
        marketplaceItemId,
        collectionItemId,
        initialMessage,
    }) => {
        const authUserId = getAuthUserId() ?? Number(user?.userId);
        if (!Number.isFinite(authUserId) || authUserId <= 0) {
            throw new Error('Please log in to send a message.');
        }

        const otherId = Number(otherUserId);
        if (!Number.isFinite(otherId) || otherId <= 0) {
            throw new Error('Missing recipient for this conversation.');
        }
        if (otherId === authUserId) {
            throw new Error('You cannot message yourself.');
        }

        const trimmed = initialMessage?.trim() || '';
        if (!trimmed) {
            throw new Error('Write a message before starting a chat.');
        }

        const payload = {
            userId: String(authUserId),
            otherUserId: otherId,
            initialMessage: trimmed,
        };
        const marketId = Number(marketplaceItemId);
        const collectibleId = Number(collectionItemId);
        if (Number.isFinite(marketId) && marketId > 0) payload.marketplaceItemId = marketId;
        if (Number.isFinite(collectibleId) && collectibleId > 0) payload.collectionItemId = collectibleId;

        const data = await chatPost('StartConversation', payload);
        const conversationId = conversationIdFrom(data);
        if (!conversationId) {
            throw new Error(data?.error || data?.Error || 'Could not start conversation.');
        }

        setDraftConversation(null);
        await fetchConversations({ force: true });
        openMessages(conversationId);
        return { ...data, conversationId };
    }, [user, fetchConversations, openMessages]);

    const selectConversation = useCallback((conversationId) => {
        setDraftConversation(null);
        setActiveConversationId(conversationId ?? null);
    }, []);

    const value = useMemo(() => ({
        conversations,
        unreadTotal,
        isOpen,
        activeConversationId,
        draftConversation,
        loading,
        setActiveConversationId: selectConversation,
        fetchConversations,
        openMessages,
        closeMessages,
        clearDraftConversation,
        toggleMessages,
        openDraftConversation,
        startConversation,
    }), [
        conversations,
        unreadTotal,
        isOpen,
        activeConversationId,
        draftConversation,
        loading,
        selectConversation,
        fetchConversations,
        openMessages,
        closeMessages,
        clearDraftConversation,
        toggleMessages,
        openDraftConversation,
        startConversation,
    ]);

    return (
        <MessagesContext.Provider value={value}>
            {children}
        </MessagesContext.Provider>
    );
};
